import { inject, Injectable } from '@angular/core';
import { AllergyIntolerance } from 'fhir/r4b';
import { Immunization } from 'fhir/r4b';

import { StateService } from './state.service';
import { UtilityService } from './utility.service';
import { AuthService } from './auth/auth.service';

@Injectable({
    providedIn: 'root'
})
export class FhirConversionService {
    private stateService = inject(StateService);
    private utilityService = inject(UtilityService);
    private authService = inject(AuthService);

    /**
     * Converts Dynamic Form data to FHIR AllergyIntolerance
     */
    allergyFormToFhir(formData: any): AllergyIntolerance {
        const allergy: AllergyIntolerance = {
            resourceType: 'AllergyIntolerance',
            patient: { reference: 'Patient/placeholder' },
            // clinicalStatus: this.stateService.buildCodeableConcept(formData.clinicalStatus),
            verificationStatus: this.utilityService.toCodeableConcept(formData.verificationStatus),
            type: formData.type,
            clinicalStatus: formData.clinicalStatus ? this.utilityService.toCodeableConcept(formData.clinicalStatus) : {
                coding: [{
                    system: 'http://terminology.hl7.org/CodeSystem/allergyintolerance-clinical',
                    code: 'active',
                    display: 'Active'
                }],
                text: 'Active'
            },
            category: formData.category ? [formData.category] : undefined,
            criticality: formData.criticality,
            code: this.utilityService.toCodeableConcept(formData.code) || { text: formData.allergyName },
            onsetDateTime: formData.onsetString,
            lastOccurrence: formData.lastOccurrence,
            note: formData.note ? [{ text: formData.note }] : undefined
        };

        return this.attachAppStateToAllergy(allergy);
    }

    /**
     * Preps FHIR AllergyIntolerance for ResourceDataReviewComponent
     */
    allergyFhirToReviewFormat(allergy: AllergyIntolerance, formFields: any[]): any {
        const reviewData: any = {};

        // Map FHIR elements back to the flat keys used in the form
        reviewData.clinicalStatus = allergy.clinicalStatus;
        reviewData.verificationStatus = allergy.verificationStatus;
        reviewData.type = allergy.type;
        reviewData.category = allergy.category?.[0];
        reviewData.criticality = allergy.criticality;
        reviewData.code = allergy.code;
        reviewData.allergyName = allergy.code?.text || allergy.code?.coding?.[0]?.display;
        reviewData.onsetString = allergy.onsetDateTime;
        reviewData.lastOccurrence = allergy.lastOccurrence;
        reviewData.note = allergy.note?.[0]?.text;

        return reviewData;
    }

    /**
     * Automatically attaches Patient, Recorder and RecordedDate
     */
    attachAppStateToAllergy(allergy: AllergyIntolerance): AllergyIntolerance {
        const patientRef = this.stateService.getPatientReference();
        if (patientRef) {
            allergy.patient = patientRef;
        }

        const practitionerRef = this.stateService.getPractitionerReference();
        if (practitionerRef) {
            allergy.recorder = practitionerRef;
            allergy.asserter = practitionerRef; // Defaulting asserter to recorder
        }

        allergy.recordedDate = new Date().toISOString();

        return allergy;
    }

    /**
     * Converts Dynamic Form data to FHIR Immunization
     */
    immunizationFormToFhir(formData: any): Immunization {
        const immunization: Immunization = {
            resourceType: 'Immunization',
            patient: { reference: 'Patient/placeholder' },
            status: formData.status?.status || 'completed',
            statusReason: formData.status?.statusReason ? this.utilityService.toCodeableConcept(formData.status.statusReason) : undefined,
            vaccineCode: this.utilityService.toCodeableConcept(formData.vaccineCode) || { text: 'Unknown Vaccine' },
            occurrenceDateTime: formData.occurrenceDateTime || new Date().toISOString(),
            primarySource: true,
            // informationSource: formData.informationSource ? this.utilityService.toCodeableConcept(formData.informationSource) : undefined,
            site: formData.bodySite ? this.utilityService.toCodeableConcept(formData.bodySite) : undefined,
            route: formData.route ? this.utilityService.toCodeableConcept(formData.route) : undefined,
            doseQuantity: formData.doseQuantity && formData.doseQuantity.value
                && formData.doseQuantity.unit ? {
                value: formData.doseQuantity.value ? Number(formData.doseQuantity.value) : undefined,
                unit: formData.doseQuantity.unit
            } : undefined,
            reasonCode: formData.reasonCode ? [this.utilityService.toCodeableConcept(formData.reasonCode)] : undefined,
            // protocolApplied: formData.targetDisease ? [{
            //     targetDisease: [this.utilityService.toCodeableConcept(formData.targetDisease)],
            //     doseNumber: '1' // Default or derived
            // }] : undefined
        };

        return this.attachAppStateToImmunization(immunization);
    }

    /**
     * Automatically attaches Patient, Recorder and Performer
     */
    attachAppStateToImmunization(immunization: Immunization): Immunization {
        const patientRef = this.stateService.getPatientReference();
        if (patientRef) {
            immunization.patient = patientRef as any;
        }

        const practitionerRef = this.stateService.getPractitionerReference(this.authService.user.getValue()?.['fhirResourceId'] ||
            this.authService.user.getValue()?.['fhirResourceId']
        );
// $#$        // /* alert(JSON.stringify(practitionerRef) */)
        if (practitionerRef) {
            immunization.performer = [{
                actor: practitionerRef as any
            }];
        }

        const encounterRef = this.stateService.getCurrentEncounterReference();
        if (encounterRef) {
            immunization.encounter = encounterRef as any;
        }

        return immunization;
    }
}
